import {
  parseArtifactHandles,
  type WorkspaceAgentArtifactListHandle,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/artifactHandleMetadata';
import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';

export type { WorkspaceAgentArtifactListHandle };

const EMPTY_HANDLES: ReadonlyMap<string, WorkspaceAgentArtifactListHandle> = new Map();

const handleKey = (type: WorkspaceAgentArtifactListHandle['type'], id: string) => `${type}:${id}`;

/**
 * `metadata.artifacts` is the count handle for an `@[assetList:]` / `@[boardList:]` citation.
 * Membership stays on the rows endpoint; the handle is what keeps a 2,124-row list from reading
 * as the first page.
 */
export const readWorkspaceAgentArtifactHandles = (
  metadata: Record<string, unknown> | null,
): ReadonlyMap<string, WorkspaceAgentArtifactListHandle> => {
  const parsed = parseArtifactHandles(metadata);

  if (parsed.length === 0) {
    return EMPTY_HANDLES;
  }

  const handles = new Map<string, WorkspaceAgentArtifactListHandle>();

  for (const handle of parsed) {
    handles.set(handleKey(handle.type, handle.id), handle);
  }

  return handles;
};

/** Raises a synthesized list pill to the handle's count. A smaller handle never shrinks the pill. */
export const withWorkspaceAgentArtifactHandleCount = (
  pill: WorkspaceAgentMessagePill,
  handles: ReadonlyMap<string, WorkspaceAgentArtifactListHandle>,
): WorkspaceAgentMessagePill => {
  if (pill.type !== 'assetList' && pill.type !== 'boardList') {
    return pill;
  }

  const handle = handles.get(handleKey(pill.type, pill.id));

  if (!handle || handle.count <= pill.count) {
    return pill;
  }

  return { ...pill, count: handle.count };
};
